import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import Logo from "@/components/site/Logo";
import { BRANDS, COMPANY } from "@/lib/brands";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-aubergine-deep text-parchment overflow-hidden grain-overlay"
    >
      <div
        aria-hidden
        className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-magenta/20 blur-[120px]"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-aubergine-light/40 blur-[120px]"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-24 pb-12">
        {/* Signature line */}
        <div className="mb-16 md:mb-20">
          <h3 className="font-display italic font-light text-4xl md:text-6xl lg:text-7xl tracking-[-0.02em] text-balance text-parchment/95 max-w-4xl leading-[1.05]">
            Honest nutrition,{" "}
            <span className="not-italic text-magenta">quietly perfected.</span>
          </h3>
        </div>

        <div className="grid gap-14 md:grid-cols-12 border-t border-parchment/15 pt-16">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <Logo className="h-14 w-14" />
              <div>
                <div className="font-display text-2xl tracking-[0.02em] uppercase text-parchment">
                  RSA OVERSEAS
                </div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-parchment/60 mt-1.5">
                  <span className="font-bold text-parchment">PRIVATE LIMITED</span>
                  <span className="mx-1.5 text-magenta">·</span>
                  <span className="font-semibold">SINCE {COMPANY.est}</span>
                </div>
              </div>
            </div>
            <p className="mt-8 max-w-md text-parchment/60 leading-relaxed">
              A Pune-based house of importers, exporters and formulators of
              premium animal nutrition and human-grade pantry foods. Trusted
              by {COMPANY.trustedBy} clients across India and the world.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.28em] text-magenta/80 font-semibold mb-6">
              Navigate
            </div>
            <ul className="space-y-3 text-parchment/80 font-display text-lg">
              <li>
                <Link
                  to="/"
                  className="hover:text-magenta transition-colors italic"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="hover:text-magenta transition-colors italic"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/brands"
                  className="hover:text-magenta transition-colors italic"
                >
                  Our Brands
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="hover:text-magenta transition-colors italic"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-magenta/80 font-semibold mb-6">
              Reach Us
            </div>
            <ul className="space-y-5 text-parchment/80">
              <li className="flex gap-3">
                <MapPin className="h-5 w-5 shrink-0 text-magenta mt-0.5" strokeWidth={1.5} />
                <span className="leading-relaxed">{COMPANY.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone className="h-5 w-5 shrink-0 text-magenta mt-0.5" strokeWidth={1.5} />
                <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-magenta">
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="h-5 w-5 shrink-0 text-magenta mt-0.5" strokeWidth={1.5} />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-magenta">
                  {COMPANY.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-parchment/15 flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-xs">
          <div className="text-parchment/45">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved. ·
            GST {COMPANY.gst}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-parchment/60">
            {BRANDS.map((b) => (
              <Link
                key={b.slug}
                to={`/brands/${b.slug}`}
                className="hover:text-magenta italic font-display"
              >
                {b.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
